import React, { useCallback } from 'react'

import { useRemoteStrings } from '@acorns/web-utils'

import type { BlueprintButtonComponent } from '@/grove-blueprints/generated-types'
import { Button } from '@/grove/components'

import { useBlueprintBindings } from '../utils/blueprint-bindings-context'
import type { GroveBlueprintComponentProps } from '../utils/grove-blueprint-component-props'
import { resolveModifiers } from '../utils/resolve-modifiers'
import { resolveTextValue } from '../utils/resolve-text-value'

type ButtonStyle = NonNullable<BlueprintButtonComponent['props']['style']>

type ButtonIntent = 'primary' | 'secondary'

const BUTTON_INTENT: Record<ButtonStyle, ButtonIntent> = {
  primary: 'primary',
  secondary: 'secondary',
}

function resolveButtonIntent(style: ButtonStyle | undefined): ButtonIntent {
  if (style === undefined) {
    return 'primary'
  }

  return BUTTON_INTENT[style]
}

export function GroveBlueprintButton({ node }: GroveBlueprintComponentProps) {
  const { getRemoteString } = useRemoteStrings()
  const { bindingValues, bindingHandlers } = useBlueprintBindings()

  const actionId = node.type === 'Button' ? node.props.action.id : undefined

  const handleClick = useCallback(() => {
    if (actionId === undefined) {
      return
    }

    const bindingHandler = bindingHandlers[actionId]

    if (bindingHandler !== undefined) {
      bindingHandler()
    } else {
      // deno-lint-ignore no-console
      console.error(`Missing binding handler for key: "${actionId}"`)
    }
  }, [actionId, bindingHandlers])

  /* v8 ignore if -- @preserve */
  if (node.type !== 'Button') {
    return null
  }

  const { className, style, attributes } = resolveModifiers(
    node.modifiers,
    bindingValues,
    (key: string) => getRemoteString({ key }),
  )

  const text = resolveTextValue(
    node.props.text,
    (key: string) => getRemoteString({ key }),
    bindingValues,
  )

  return (
    <div className={className || undefined} style={style} {...attributes}>
      <Button
        intent={resolveButtonIntent(node.props.style)}
        onClick={handleClick}
      >
        {text}
      </Button>
    </div>
  )
}
